import { Injectable } from '@angular/core';
import { RestResourceModule } from './rest-resource.module';
import { RequestService } from './request.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Decimal } from 'decimal.js';

@Injectable({
    providedIn: RestResourceModule
})
export class MultiYearAuctionsResource {
    private readonly restUrl = 'multi-year-cfo-auctions';

    constructor(private requestService: RequestService) {}

    public getAuctions(): Observable<MultiYearCfoAuctionGuiDto[]> {
        return this.requestService
            .get<MultiYearCfoAuctionDto[]>(this.restUrl)
            .pipe(
                map((value) => {
                    let convertedAuctions: MultiYearCfoAuctionGuiDto[] = [];

                    for (let i = 0; i < value.length; i++) {
                        convertedAuctions.push(this.convertAuction(value[i]));
                    }

                    return convertedAuctions;
                })
            );
    }

    public deleteBid(auctionId: number, bidId: number): Observable<void> {
        return this.requestService.delete(
            this.restUrl + `/${auctionId}/bids/${bidId}`
        );
    }

    public getBidsForAuction(
        auctionId: number
    ): Observable<MultiYearCfoBidDto[]> {
        return this.requestService
            .get<MultiYearCfoBidDto[]>(this.restUrl + `/${auctionId}/bids`)
            .pipe(
                map((result) => {
                    let convertedBids: MultiYearCfoBidDto[] = [];
                    result.forEach((bid) =>
                        convertedBids.push(this.convertBid(bid))
                    );
                    return convertedBids;
                })
            );
    }

    private convertBid(bid: MultiYearCfoBidDto | any): MultiYearCfoBidDto {
        // first convert all dates
        bid.runtime.end = new Date(bid.runtime.end);
        bid.runtime.start = new Date(bid.runtime.start);

        // convert tariffs
        if (bid.tariff0) {
            bid.tariff0.value.value = new Decimal(bid.tariff0.value.value);
        }
        if (bid.tariff1) {
            bid.tariff1.value.value = new Decimal(bid.tariff1.value.value);
        }
        bid.amount.value.value = new Decimal(bid.amount.value.value);
        return bid;
    }

    private convertAuction(
        value: MultiYearCfoAuctionDto | any
    ): MultiYearCfoAuctionGuiDto {
        // first convert all dates
        value.runtime.end = new Date(value.runtime.end);
        value.runtime.start = new Date(value.runtime.start);

        value.submissionWindow.end = new Date(value.submissionWindow.end);
        value.submissionWindow.start = new Date(value.submissionWindow.start);

        // convert all decimals
        for (let j = 0; j < value.tariffsList.length; j++) {
            let tariff = value.tariffsList[j];
            if (tariff.tariff0) {
                tariff.tariff0.value.value = new Decimal(
                    tariff.tariff0.value.value
                );
            }
            if (tariff.tariff1) {
                tariff.tariff1.value.value = new Decimal(
                    tariff.tariff1.value.value
                );
            }
            if (tariff.additionalFee0) {
                tariff.additionalFee0.value.value = new Decimal(
                    tariff.additionalFee0.value.value
                );
            }
            if (tariff.additionalFee1) {
                tariff.additionalFee1.value.value = new Decimal(
                    tariff.additionalFee1.value.value
                );
            }
        }
        value.minimumLotSize.value.value = new Decimal(
            value.minimumLotSize.value.value
        );
        value.marketableAmount.value.value = new Decimal(
            value.marketableAmount.value.value
        );

        // and add new fields
        return Object.assign(value, {
            tsoNames: value.tso1Name
                ? `${value.tso0Name} (${value.tso0ShortName}) / ${value.tso1Name} (${value.tso1ShortName})`
                : `${value.tso0Name} (${value.tso0ShortName})`,
            startingPrice: this.getStartingPrice(value)
        }) as MultiYearCfoAuctionGuiDto;
    }

    public getAuction(id: number): Observable<MultiYearCfoAuctionGuiDto> {
        return this.requestService
            .get<MultiYearCfoAuctionDto>(`${this.restUrl}/${id}`)
            .pipe(map((value) => this.convertAuction(value)));
    }

    private getStartingPrice(auction: MultiYearCfoAuctionDto): Decimal {
        let minPrice;
        auction.tariffsList.forEach((tariff) => {
            let price = new Decimal(0);
            if (tariff.tariff0) {
                price = price.plus(tariff.tariff0.value.value);
            }
            if (tariff.tariff1) {
                price = price.plus(tariff.tariff1.value.value);
            }
            if (tariff.additionalFee0) {
                price = price.plus(tariff.additionalFee0.value.value);
            }
            if (tariff.additionalFee1) {
                price = price.plus(tariff.additionalFee1.value.value);
            }
            if (!minPrice) {
                minPrice = price;
            }
            if (price.lessThan(minPrice)) {
                minPrice = price;
            }
        });
        return minPrice;
    }

    public placeBid(
        auctionId: number,
        bid: MultiYearCfoBidDto
    ): Observable<MultiYearCfoBidDto> {
        return this.requestService
            .post<MultiYearCfoBidDto>(`${this.restUrl}/${auctionId}/bids`, bid)
            .pipe(map((value) => this.convertBid(value)));
    }

    public updateBid(
        auctionId: number,
        bidId: number,
        bid: MultiYearCfoBidDto
    ) {
        return this.requestService
            .put<MultiYearCfoBidDto>(
                `${this.restUrl}/${auctionId}/bids/${bidId}`,
                bid
            )
            .pipe(map((value) => this.convertBid(value)));
    }
}

export interface MultiYearCfoAuctionGuiDto extends MultiYearCfoAuctionDto {
    tsoNames: string;
    startingPrice: number | Decimal;
}
